/**
 * Notification helper — wraps Sonner toast calls with a global enable/disable flag.
 * The setting is persisted in localStorage keyed by wallet address (or a shared key
 * when no wallet is connected).
 */
import { toast } from 'sonner';

const GLOBAL_KEY = 'seekscape_notifications_enabled';

/** Returns the localStorage key for the given wallet address (or global). */
function storageKey(walletAddress?: string | null): string {
  return walletAddress ? `${GLOBAL_KEY}_${walletAddress}` : GLOBAL_KEY;
}

/** Returns whether notifications are enabled for the given wallet. Defaults to true. */
export function getNotificationsEnabled(walletAddress?: string | null): boolean {
  try {
    const raw = localStorage.getItem(storageKey(walletAddress));
    if (raw === null) return true; // default on
    return raw !== 'false';
  } catch {
    return true;
  }
}

/** Persists the notification preference for the given wallet. */
export function setNotificationsEnabled(enabled: boolean, walletAddress?: string | null): void {
  try {
    localStorage.setItem(storageKey(walletAddress), String(enabled));
  } catch {
    // Ignore storage errors
  }
}

/** Thin wrappers around sonner toast that respect the notifications toggle. */
export const notify = {
  error(msg: string, walletAddress?: string | null): void {
    if (getNotificationsEnabled(walletAddress)) toast.error(msg);
  },
  info(msg: string, walletAddress?: string | null): void {
    if (getNotificationsEnabled(walletAddress)) toast.info(msg);
  },
};
